import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import ButtonCTA from '@/components/base/ButtonCTA';
import ButtonWhatsApp from '@/components/base/ButtonWhatsApp';
import ButtonPhone from '@/components/base/ButtonPhone';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import PriceTable from '@/components/feature/PriceTable';
import FAQAccordion from '@/components/feature/FAQAccordion';
import CTABlock from '@/components/feature/CTABlock';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import RevealWrapper from '@/components/base/RevealWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { REASSURANCE_ITEMS } from '@/mocks/homeData';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { localBusiness, website, service, faqPage, breadcrumb, article } from '@/lib/seo';

const PRICING_ROWS = [
  { service: 'Dératisation logement (traitement + suivi)', price: '75 € – 260 €' },
  { service: 'Infestation importante, plusieurs zones', price: '200 € – 400 €' },
  { service: 'Contrat annuel commerce/restaurant (passages + registre HACCP)', price: 'Sur devis, dès 40 €/mois' },
];

const FAQ_ITEMS = [
  {
    question: 'Combien coûte une dératisation en Guadeloupe ?',
    answer: 'Entre 75 € et 260 € TTC pour un logement standard, traitement et suivi compris ; jusqu\'à 400 € pour une infestation lourde. Contrats professionnels dès 40 €/mois. Devis gratuit et prix ferme avant intervention.',
  },
  {
    question: 'En combien de temps intervenez-vous ?',
    answer: 'Sous 24 à 48 h partout en Guadeloupe, souvent le jour même pour les urgences (commerce alimentaire, forte présence).',
  },
  {
    question: 'Les produits sont-ils dangereux pour les enfants et les animaux ?',
    answer: 'Les appâts sont placés dans des postes sécurisés fermés à clé. En présence d\'animaux ou en cuisine, notre technicien privilégie les pièges mécaniques. Tout est expliqué avant le traitement.',
  },
  {
    question: 'Pourquoi mes produits de supermarché ne marchent pas ?',
    answer: 'Beaucoup de rats sont devenus résistants ou méfiants (néophobie) face aux appâts grand public. Nous utilisons des matières actives réservées au Certibiocide et des placements que le rat ne contourne pas.',
  },
  {
    question: 'Rat ou souris, comment savoir ?',
    answer: 'Crottes en grain de riz, bruits légers, passages minuscules : souris. Crottes en olive, bruits lourds dans le galetas, traces de gras, câbles sectionnés : rat — le plus souvent un rat noir, excellent grimpeur. Le traitement diffère, d\'où le diagnostic.',
  },
  {
    question: 'Les rats reviennent-ils après le traitement ?',
    answer: 'Pas si la prévention est faite : c\'est l\'intérêt du colmatage des accès et des conseils inclus dans nos interventions.',
  },
  {
    question: 'Que faire en attendant le technicien ?',
    answer: 'Stockez tous les aliments dans des contenants rigides fermés, fermez les poubelles, et ne bouchez pas encore les trous : le technicien doit lire les passages pour placer le traitement au bon endroit.',
  },
];

const STEPS = [
  {
    number: '1',
    icon: 'ri-search-eye-line',
    title: 'Diagnostic sur place',
    description: 'Notre technicien inspecte points d\'entrée (toiture, gaines, canalisations, végétation en contact avec le toit), traces d\'activité, espèce et taille de la colonie — on ne traite pas un rat noir des combles comme un rat d\'égout.',
  },
  {
    number: '2',
    icon: 'ri-medicine-bottle-line',
    title: 'Traitement adapté',
    description: 'Postes d\'appâtage sécurisés inaccessibles aux enfants et animaux, pièges mécaniques quand la situation l\'exige, produits professionnels réservés au Certibiocide — sans commune mesure avec les produits de supermarché, contre lesquels les rats développent des résistances.',
  },
  {
    number: '3',
    icon: 'ri-shield-flash-line',
    title: 'Prévention',
    description: 'Colmatage des points d\'entrée, grilles sur évacuations, conseils stockage et déchets. Sans cette étape, la réinfestation est garantie à la saison des pluies suivante.',
  },
  {
    number: '4',
    icon: 'ri-refresh-line',
    title: 'Suivi',
    description: 'Passage de contrôle jusqu\'à éradication complète, compte-rendu d\'intervention fourni (obligatoire pour les pros soumis au plan HACCP).',
  },
];

export default function Deratisation() {
  useDocumentHead({
    title: 'Dératisation Guadeloupe : rats & souris, intervention 24-48h',
    description: 'Dératisation à domicile partout en Guadeloupe par nos techniciens certifiés Certibiocide. Devis gratuit, tarifs dès 75 €, intervention sous 24-48 h.',
    jsonLd: [service({ name: 'Dératisation en Guadeloupe', serviceType: 'Dératisation', path: '/deratisation-guadeloupe', description: 'Élimination des rats et souris à domicile partout en Guadeloupe.' }), faqPage(FAQ_ITEMS), breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Dératisation', path: '/deratisation-guadeloupe' }])],
  });

  return (
    <main>
      <Header />
      <Breadcrumb items={[{label:'Accueil',href:'/'},{label:'Dératisation'}]} />

      {/* HERO */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/deratisation-1.webp"
            alt="Technicien dératiseur inspectant les poutres d'un galetas de maison créole à la recherche de rats en Guadeloupe"
            className="w-full h-full object-cover object-top"
           loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,58,46,0.94), rgba(11,58,46,0.74) 52%, rgba(11,58,46,0.42))' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,58,46,0.55), transparent 38%, transparent 82%, rgba(11,58,46,0.38))' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-3xl md:text-5xl text-white leading-tight mb-6">
              Dératisation en Guadeloupe : débarrassez-vous des rats et des souris, durablement
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
              Des grattements dans le galetas la nuit ? Des crottes noires derrière le réfrigérateur, des sacs de riz éventrés, des câbles rongés ? En Guadeloupe, une invasion de rats ne se règle pas toute seule : avec la chaleur toute l&rsquo;année et l&rsquo;humidité de la saison des pluies, une famille de rats devient une colonie en quelques semaines. Nos techniciens certifiés Certibiocide interviennent sous 24 à 48 h, partout en Guadeloupe.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 flex-wrap">
              <ButtonCTA>Devis gratuit</ButtonCTA>
              <ButtonWhatsApp>WhatsApp direct</ButtonWhatsApp>
              <ButtonPhone />
            </div>
          </div>
        </div>
      </section>

      {/* RÉASSURANCE */}
      <ReassuranceBar items={REASSURANCE_ITEMS} />

      {/* POURQUOI LES RATS PROLIFÈRENT */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                  Pourquoi les rats prolifèrent en Guadeloupe
                </h2>
                <div className="text-foreground-600 text-base leading-relaxed space-y-4">
                  <p>
                    Le climat tropical d&rsquo;abord : pas d&rsquo;hiver pour freiner la reproduction — un couple de rats peut engendrer plusieurs centaines de descendants en un an. La saison cyclonique ensuite : de juin à novembre, les fortes pluies poussent les rats d&rsquo;égout à remonter vers les maisons, commerces et restaurants. Le carême enfin : en période sèche, rats des champs et rats noirs (l&rsquo;espèce reine de nos galetas, excellente grimpeuse) entrent chercher l&rsquo;eau et la nourriture.
                  </p>
                  <p>
                    Le phénomène est tel que le Département a lancé une campagne collective de dératisation agricole — ce qui se passe dans les champs finit dans les bourgs, et dans votre cuisine.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="w-full h-72 md:h-80 rounded-xl overflow-hidden">
                  <img
                    src="/images/deratisation-2.webp"
                    alt="Inspection d'un galetas de maison créole pour détecter les signes de rats en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* LEPTOSPIROSE — encadré accent */}
      <section className="bg-background-100 py-12 md:py-16">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="bg-secondary-100 border-l-4 border-secondary-500 rounded-r-xl p-6 md:p-8">
              <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
                Le vrai danger : la leptospirose
              </h3>
              <p className="text-foreground-700 text-base leading-relaxed mb-4">
                La Guadeloupe est l&rsquo;une des régions de France les plus touchées par la leptospirose, maladie bactérienne transmise par l&rsquo;urine des rats via l&rsquo;eau stagnante, la terre humide ou les surfaces souillées. Des dizaines de cas sont recensés chaque année sur l&rsquo;île, dont des formes graves. Une dératisation n&rsquo;est pas un confort : c&rsquo;est une protection sanitaire pour votre famille.
              </p>
              <Link
                to="/blogue/leptospirose-guadeloupe"
                className="inline-flex items-center gap-2 text-secondary-500 font-semibold text-sm hover:text-secondary-600 transition-colors cursor-pointer"
              >
                Notre dossier leptospirose
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* LES SIGNES QUI NE TROMPENT PAS */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                Les signes qui ne trompent pas
              </h2>
              <ul className="space-y-3 text-foreground-600 text-base leading-relaxed">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Bruits de course dans le plafond la nuit</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Crottes en grain de riz (souris) ou en olive (rat) le long des murs et sous l&rsquo;évier</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Emballages rongés</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Traces de gras le long des plinthes</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Câbles ou gaines rongés — première cause d&rsquo;incendie électrique</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Odeur d&rsquo;ammoniac</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Votre chien ou chat qui fixe un point du plafond</span>
                </li>
              </ul>
              <p className="text-foreground-600 text-base leading-relaxed mt-4">
                Et un rat aperçu en plein jour = colonie déjà nombreuse (le rat est nocturne).
              </p>
              <div className="mt-6">
                <Link
                  to="/blogue/rat-dans-la-maison-que-faire"
                  className="inline-flex items-center gap-2 text-secondary-500 font-semibold text-sm hover:text-secondary-600 transition-colors cursor-pointer"
                >
                  Rat dans la maison : les bons réflexes
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* COMMENT SE DÉROULE L'INTERVENTION */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Comment se déroule notre intervention
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-12">
              <div>
                <p className="text-foreground-600 text-base leading-relaxed">
                  De l&rsquo;inspection au suivi, nos interventions suivent un protocole rigoureux en 4 étapes. Chaque passage est documenté et expliqué — vous savez exactement ce qui est fait, où, et pourquoi.
                </p>
              </div>
              <div className="relative">
                <div className="w-full h-64 md:h-72 rounded-xl overflow-hidden">
                  <img
                    src="/images/deratisation-3.webp"
                    alt="Installation d'un poste d'appâtage sécurisé anti-rats près des fondations d'une maison créole en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {STEPS.map((step) => (
                <div key={step.number} className="bg-white rounded-xl p-6 border border-background-200/70">
                  <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className={`${step.icon} text-secondary-500 text-xl w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <div className="w-7 h-7 mb-3 flex items-center justify-center rounded-full bg-primary-500 text-white font-heading font-bold text-xs">
                    {step.number}
                  </div>
                  <h3 className="font-heading font-bold text-lg text-foreground-950 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* TARIFS */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Nos tarifs de dératisation
              </h2>
            </div>
            <div className="max-w-2xl mx-auto">
              <PriceTable rows={PRICING_ROWS} />
              <p className="text-center text-foreground-500 text-sm mt-4">
                Prix ferme annoncé avant intervention, devis gratuit.
              </p>
              <div className="text-center mt-6">
                <Link
                  to="/tarifs"
                  className="inline-flex items-center gap-2 text-secondary-500 font-semibold hover:text-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Voir tous les tarifs
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* PARTICULIERS ET PROFESSIONNELS */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-10 text-center">
              Particuliers et professionnels
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-background-200/70">
                <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-full bg-mango-500/20">
                  <i className="ri-home-4-line text-mango-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-950 mb-3">Particuliers</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  Maisons, appartements, cases, dépendances, poulaillers, jardins — partout en Guadeloupe, de Basse-Terre à Grande-Terre.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-background-200/70">
                <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-full bg-mango-500/20">
                  <i className="ri-restaurant-line text-mango-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-950 mb-3">Professionnels</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  La réglementation impose aux restaurants, snacks et commerces alimentaires un plan de lutte contre les nuisibles documenté (registre, plan d&rsquo;appâtage, passages réguliers). Nos contrats annuels incluent le compte-rendu conforme HACCP — indispensable en cas de contrôle sanitaire.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-background-200/70">
                <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-full bg-mango-500/20">
                  <i className="ri-building-2-line text-mango-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-950 mb-3">Syndics et bailleurs</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  Parties communes, caves, locaux poubelles — le règlement sanitaire départemental impose de maintenir les immeubles exempts de rongeurs.
                </p>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* FAQ */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Questions fréquentes sur la dératisation
              </h2>
            </div>
            <div className="max-w-3xl mx-auto">
              <FAQAccordion items={FAQ_ITEMS} />
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CTA FINAL */}
      <CTABlock
        title="Des rats chez vous ? Photo sur WhatsApp → prix ferme en 2 h."
      />

      {/* MAILLAGE INTERNE */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="text-center text-foreground-500 text-sm uppercase tracking-wider mb-4">À lire aussi</p>
            <Link
              to="/traitement-ravets-cafards-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Des ravets en plus des rats ?
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Les deux profitent des mêmes accès
              </span>
            </Link>
            <Link
              to="/tarifs"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Voir tous nos tarifs
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Prix transparents, devis gratuit
              </span>
            </Link>
            <Link
              to="/blogue/leptospirose-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Notre dossier leptospirose
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                La maladie transmise par les rats en Guadeloupe
              </span>
            </Link>
            <Link
              to="/blogue/rat-dans-la-maison-que-faire"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Rat dans la maison : que faire
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Les bons réflexes avant l&rsquo;arrivée du technicien
              </span>
            </Link>
          </div>
        </div>
      </section>

      <ContactFormBlock source="deratisation" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}